import { useCallback, useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { EmailPreview } from "@shared-core/data/marketing-types";

type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "not_sent" }
  | { status: "ready"; preview: EmailPreview };

export function CampaignEmailPreviewTab({
  campaignId,
  stepId,
}: {
  campaignId: string;
  stepId: string;
}) {
  const adapter = useDataAdapter();
  const [state, setState] = useState<State>({ status: "loading" });

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const result = await adapter.getCampaignEmailPreview(campaignId, stepId);
      setState(
        result ? { status: "ready", preview: result } : { status: "not_sent" },
      );
    } catch (e) {
      console.error("marketing.campaign.email_preview.load.failed", e);
      setState({ status: "error" });
    }
  }, [adapter, campaignId, stepId]);

  useEffect(() => {
    load();
  }, [load]);

  if (state.status === "loading") {
    return (
      <div className="text-sm text-muted-foreground">Loading preview…</div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600">
        Couldn't load the email preview. Please try again.
      </div>
    );
  }

  if (state.status === "not_sent") {
    return (
      <div className="p-8 text-center border border-dashed border-border rounded-lg">
        <p className="text-sm text-muted-foreground">
          No preview of this email yet. It shows once marketing links the send
          to its Mailchimp campaign.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-muted/30">
      <iframe
        title="Sent email preview"
        // Sandboxed with no permissions: this renders untrusted HTML fetched
        // from Mailchimp, so scripts, same-origin access, and top-level
        // navigation are all denied.
        sandbox=""
        srcDoc={state.preview.html_content}
        className="w-full min-h-[600px] bg-white"
      />
    </div>
  );
}
